// Karbantartás és rendszerállapot — négy kérdésre válaszol egy lapon:
//   1. MŰKÖDIK-E a rendszer (életjel: Odin-kapcsolat, keresőindex, futásidő),
//   2. MENNYI tartalom van, és ebből MI LÁTSZIK KÍVÜLRŐL (belső ⇄ nyilvános szembeállítás),
//   3. rendben van-e a KERESŐINDEX, és újraépíthető-e (`knowledge.admin`),
//   4. MIT NEM TALÁLNAK meg az emberek (hiánynapló, `gap.review` VAGY `knowledge.admin`).
//
// A 2. pont nem statisztikai dísz: 2026-08-24-én élő hibaként jelent meg, hogy egy nyilvánosra
// állított cikk mégsem látszik a súgón — mert a szekció/könyv belső maradt. A szembeállított
// számok ezt egy pillantásra megmutatják, mielőtt bárki hibát keresne.
//
// A KÉT SZEKCIÓ KÜLÖN JOGON ÁLL, mert a katalógus is így osztja őket: az index újraépítése
// üzemeltetői, drága művelet (superadmin tier), a hiánynapló viszont szerkesztőségi olvasás — azt
// a kolléga nézi, aki a hiányzó cikket megírja (az admin tier is megkapja). Aki csak az egyiket
// viszi, csak azt a szekciót látja, és a másik végpontot le sem kérdezzük (a 403 különben
// „nem tölthető be" hibaként jelenne meg, vagyis a jog-hiányt működési hibának hazudnánk).
//
// Miért kell: a három admin-végpont eddig CSAK parancssorból volt elérhető, és ez nem elméleti
// hiányosság — 2026-08-07-én élesben az index üresen állt, a keresés mindenre 0 találatot adott,
// és nem volt hol megnézni sem az állapotot, sem újraépíteni. Ez az oldal zárja be azt a kört.
//
// ⛔ HATÓKÖR: kizárólag index- és keresés-üzemeltetés. Jog-/felhasználókezelő képernyő NEM készül
// — a szerep- és jog-kiosztás az Atlasban történik (platform-döntés, Szilárd 2026-08-03).
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { SpaceNode } from '@morgens-hub/contracts';
import { PERMISSIONS } from '@morgens-hub/shared';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AppShell } from '@/components/shared/app-shell';
import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useIndexStatus, useRebuildIndex, useSearchMisses } from '@/features/admin/api';
import { useKnowledgeTree } from '@/features/knowledge/api';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { usePermissionState } from '@/lib/use-permission';

export const Route = createFileRoute('/uzemeltetes')({
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: OperationsPage,
});

const dateFormat = new Intl.DateTimeFormat('hu-HU', { dateStyle: 'medium', timeStyle: 'short' });
const numberFormat = new Intl.NumberFormat('hu-HU');

function OperationsPage(): JSX.Element {
  const { t } = useTranslation();
  const session = api.session.useQuery(['session']);
  // Ugyanaz a session-cache-kulcs mindkét lekérdezésnél → nincs plusz kérés.
  const adminPermission = usePermissionState(PERMISSIONS.knowledgeAdmin);
  const gapPermission = usePermissionState(PERMISSIONS.gapReview);
  const canAdmin = adminPermission.allowed;
  // A teljes tudástár-admin a hiánynaplót is látja (a BFF-kapu is így OR-ozik).
  const canReviewGaps = gapPermission.allowed || canAdmin;
  const status = useIndexStatus(canAdmin);
  const misses = useSearchMisses(canReviewGaps);
  const rebuild = useRebuildIndex();
  // Életjel + navigációs fa: MINDKETTŐ meglévő végpont, tehát az áttekintő nem hoz új
  // szerver-felületet. A fa a belső közönségé (a szerver szűri), és minden csomópontra megadja a
  // szerver által számolt EFFEKTÍV nyilvánosságot (`publiclyVisible`) — a nyilvánosság-szabály
  // tehát NEM itt él, csak megjelenítjük.
  const health = api.health.useQuery(['health']);
  const tree = useKnowledgeTree();

  const sessionBody = session.data?.status === 200 ? session.data.body : null;
  const index = status.data?.status === 200 ? status.data.body : null;
  const missList = misses.data?.status === 200 ? misses.data.body.misses : null;
  const healthBody = health.data?.status === 200 ? health.data.body : null;
  const content = summarizeContent(tree.data?.status === 200 ? tree.data.body.spaces : null);

  // A jog-kapu a BFF-ben van (403); ez csak azt kerüli el, hogy egy jogosulatlan felhasználó
  // értelmezhetetlen hibákkal teli oldalt lásson.
  //
  // A `isLoading` külön ág: hidegindításkor a néma token-megújítás még fut, a session MÉG nem áll —
  // ilyenkor várni kell, nem „nincs jogod"-ot villantani a jogosultnak.
  const permissionsLoading = adminPermission.isLoading || gapPermission.isLoading;
  if (!permissionsLoading && !canAdmin && !canReviewGaps) {
    return (
      <AppShell session={sessionBody}>
        <div className="mx-auto max-w-3xl">
          <KnowledgeCrumbs current={t('operations.title')} />
          <h1 className="text-read-2xl font-semibold tracking-tight">{t('operations.title')}</h1>
          <p className="text-muted-foreground mt-4 text-sm">{t('operations.noAccess')}</p>
        </div>
      </AppShell>
    );
  }

  const startRebuild = (): void => {
    rebuild.mutate(
      { body: {} },
      {
        // ts-rest + react-query: az `onSuccess`-be CSAK 2xx jut, a contract szerint itt csak 200
        // lehet — a hibás státuszok (409/503) az `onError`-ban landolnak.
        onSuccess: (res) => {
          toast.success(
            t('operations.rebuildDone', {
              articles: numberFormat.format(res.body.articles),
              chunks: numberFormat.format(res.body.chunks),
            }),
          );
          void status.refetch();
        },
        onError: (failure: unknown) => {
          // A 409 nem hiba, hanem állapot: épp fut egy másik menet (a kérés abba olvad bele).
          const httpStatus = (failure as { status?: number } | null)?.status;
          toast.error(
            httpStatus === 409 ? t('operations.rebuildBusy') : t('operations.rebuildFailed'),
          );
          void status.refetch();
        },
      },
    );
  };

  return (
    <AppShell session={sessionBody}>
      <div className="mx-auto max-w-3xl">
        <KnowledgeCrumbs current={t('operations.title')} />
        <header>
          <h1 className="text-read-2xl font-semibold tracking-tight">{t('operations.title')}</h1>
          <p className="text-muted-foreground text-read-sm mt-2">{t('operations.subtitle')}</p>
        </header>

        {/* ── Rendszerállapot (mindenkinek, aki a lapot látja) ──
            A publikus életjel-végpontból; nincs benne semmi, ami ne lenne amúgy is nyilvános. */}
        <section className="mt-10" aria-labelledby="rendszerallapot">
          <h2 id="rendszerallapot" className="text-read-lg font-medium">
            {t('operations.healthTitle')}
          </h2>
          {health.isLoading && <Skeleton className="mt-4 h-20 w-full" />}
          {!health.isLoading && !healthBody && (
            <p className="text-destructive mt-4 text-sm">{t('operations.healthLoadError')}</p>
          )}
          {healthBody && (
            <dl className="border-border mt-4 grid grid-cols-2 gap-x-6 gap-y-3 rounded-xl border p-5 sm:grid-cols-3">
              <Stat
                label={t('operations.healthOdin')}
                value={t(
                  healthBody.odin === 'ok'
                    ? 'operations.healthOdinOk'
                    : 'operations.healthOdinUnreachable',
                )}
                tone={healthBody.odin === 'ok' ? 'ok' : 'bad'}
              />
              <Stat
                label={t('operations.healthIndex')}
                value={t(`operations.healthIndex${healthBody.index === 'ok' ? 'Ok' : healthBody.index === 'off' ? 'Off' : 'Error'}`)}
                tone={healthBody.index === 'ok' ? 'ok' : healthBody.index === 'off' ? 'neutral' : 'bad'}
              />
              <Stat label={t('operations.healthUptime')} value={formatUptime(healthBody.uptimeSec, t)} />
            </dl>
          )}
        </section>

        {/* ── Tartalom és nyilvánosság ──
            A BELSŐ fa (`knowledgeTree`) minden mezőt megad, ami kell: darabszámok szintenként, és
            a szerver által számolt EFFEKTÍV nyilvánosság (`publiclyVisible`) — nem itt döntjük el,
            mi számít nyilvánosnak, csak megmutatjuk. */}
        <section className="mt-12" aria-labelledby="tartalom">
          <h2 id="tartalom" className="text-read-lg font-medium">
            {t('operations.contentTitle')}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">{t('operations.contentSubtitle')}</p>

          {tree.isLoading && <Skeleton className="mt-4 h-28 w-full" />}
          {!tree.isLoading && !content && (
            <p className="text-destructive mt-4 text-sm">{t('operations.contentLoadError')}</p>
          )}

          {content && (
            <>
              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="text-subtle text-left">
                    <th scope="col" className="pb-2 font-normal"> </th>
                    <th scope="col" className="pb-2 font-normal">
                      {t('operations.contentInternal')}
                    </th>
                    <th scope="col" className="pb-2 font-normal">
                      {t('operations.contentPublic')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-border divide-y">
                  <CountRow
                    label={t('operations.contentSpaces')}
                    internal={content.spaces}
                    unlocked={content.publicSpaces}
                  />
                  <CountRow
                    label={t('operations.contentCategories')}
                    internal={content.categories}
                    unlocked={content.publicCategories}
                  />
                  <CountRow
                    label={t('operations.contentArticles')}
                    internal={content.articles}
                    unlocked={content.publicArticles}
                  />
                </tbody>
              </table>

              {content.publicSpaces === 0 ? (
                <p className="text-muted-foreground mt-4 text-sm">
                  {t('operations.contentPublicNone')}
                </p>
              ) : (
                <p className="text-muted-foreground mt-4 text-sm">
                  {t('operations.contentPublicList')}:{' '}
                  <span className="text-foreground">{content.publicSpaceNames.join(', ')}</span>
                </p>
              )}
            </>
          )}
        </section>

        {/* ── Index-állapot (`knowledge.admin`) ── */}
        {canAdmin && (
          <section className="mt-10" aria-labelledby="index-allapot">
            <h2 id="index-allapot" className="text-read-lg font-medium">
              {t('operations.indexTitle')}
            </h2>

            {status.isLoading && <Skeleton className="mt-4 h-24 w-full" />}

            {!status.isLoading && !index && (
              <p className="text-destructive mt-4 text-sm">{t('operations.indexLoadError')}</p>
            )}

            {index && (
              <>
                <dl className="border-border mt-4 grid grid-cols-2 gap-x-6 gap-y-3 rounded-xl border p-5 sm:grid-cols-4">
                  <Stat
                    label={t('operations.articles')}
                    value={numberFormat.format(index.articles)}
                  />
                  <Stat label={t('operations.chunks')} value={numberFormat.format(index.chunks)} />
                  {/* I1: OPCIONÁLIS mező — régi/cache-elt válasz nem tartalmazza, ilyenkor a sáv
                    egyszerűen kihagyja ezt a statisztikát (a kontrakt visszafelé-kompatibilis). */}
                  {index.structureRows !== undefined && (
                    <Stat
                      label={t('operations.structureRows')}
                      value={numberFormat.format(index.structureRows)}
                    />
                  )}
                  <Stat
                    label={t('operations.lastRun')}
                    value={
                      index.lastRunAt
                        ? dateFormat.format(new Date(index.lastRunAt))
                        : t('operations.never')
                    }
                  />
                  <Stat
                    label={t('operations.state')}
                    value={
                      index.running
                        ? t('operations.stateRunning')
                        : index.ready
                          ? t('operations.stateReady')
                          : t('operations.stateOff')
                    }
                  />
                </dl>

                {/* A legutóbbi futás hibája: enélkül egy csendben elbukott újraépítés után az oldal
                  „rendben"-t mutatna, miközben az index elavult. */}
                {index.lastError && (
                  <p className="text-destructive mt-3 text-sm">
                    {t('operations.lastError', { error: index.lastError })}
                  </p>
                )}

                {index.chunks === 0 && !index.running && (
                  <p className="text-muted-foreground mt-3 text-sm">
                    {t('operations.emptyIndexHint')}
                  </p>
                )}

                {/* I1: a chunks és a structure_titles EGYMÁSTÓL FÜGGETLENÜL üres lehet (élesben
                  mérve 2026-08-07: a chunks tele volt, a friss structure_titles tábla mégis üres) —
                  a "kész" állapot enélkül eltakarta volna, hogy a "Címben" mód néma 0 találatot ad. */}
                {index.structureRows === 0 && index.chunks > 0 && !index.running && (
                  <p className="text-muted-foreground mt-3 text-sm">
                    {t('operations.emptyStructureHint')}
                  </p>
                )}

                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="mt-4 gap-1.5"
                  disabled={rebuild.isPending || index.running || !index.ready}
                  onClick={startRebuild}
                >
                  <ArrowPathIcon className="h-4 w-4" aria-hidden />
                  {rebuild.isPending || index.running
                    ? t('operations.rebuilding')
                    : t('operations.rebuild')}
                </Button>
                <p className="text-subtle mt-2 text-sm">{t('operations.rebuildHint')}</p>
              </>
            )}
          </section>
        )}

        {/* ── Hiánynapló (`gap.review` vagy `knowledge.admin`) ── */}
        {canReviewGaps && (
          <section className="mt-12" aria-labelledby="hianynaplo">
            <h2 id="hianynaplo" className="text-read-lg font-medium">
              {t('operations.missesTitle')}
            </h2>
            <p className="text-muted-foreground mt-1 text-sm">{t('operations.missesSubtitle')}</p>

            {misses.isLoading && <Skeleton className="mt-4 h-32 w-full" />}

            {!misses.isLoading && !missList && (
              <p className="text-destructive mt-4 text-sm">{t('operations.missesLoadError')}</p>
            )}

            {missList?.length === 0 && (
              <div className="text-muted-foreground mt-6 flex items-center gap-3 text-sm">
                <MagnifyingGlassIcon className="text-subtle h-5 w-5" aria-hidden />
                {t('operations.missesEmpty')}
              </div>
            )}

            {missList && missList.length > 0 && (
              <ul className="divide-border border-border mt-4 divide-y border-y">
                {missList.map((miss) => (
                  <li key={miss.q} className="flex flex-wrap items-baseline gap-3 py-3">
                    <span className="min-w-0 flex-1 font-medium">{miss.q}</span>
                    <span className="text-subtle text-sm tabular-nums">
                      {t('operations.missCount', { count: miss.count })}
                    </span>
                    <span className="text-subtle text-sm">
                      {dateFormat.format(new Date(miss.lastAt))}
                    </span>
                    {/*
                      A hiánynapló addig puszta lista, amíg nem lehet BELŐLE cselekedni: a
                      szerkesztőnek eddig vissza kellett lapoznia és bemásolnia a kifejezést. A link
                      a keresett szöveggel nyitja az új-cikk lapot — a cím onnantól szabadon írható.
                    */}
                    <Link
                      to="/uj-cikk"
                      search={{ title: miss.q }}
                      className="text-primary hover:text-primary/80 text-sm underline-offset-4 hover:underline"
                    >
                      {t('operations.missWriteArticle')}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  /** `ok`/`bad`: állapot-jelzés színnel. A szín SOSEM az egyetlen hordozó — a szöveg maga is kimondja. */
  tone?: 'neutral' | 'ok' | 'bad';
}): JSX.Element {
  return (
    <div>
      <dt className="text-subtle text-sm">{label}</dt>
      <dd
        className={
          tone === 'bad'
            ? 'text-destructive mt-0.5 font-medium tabular-nums'
            : 'mt-0.5 font-medium tabular-nums'
        }
      >
        {value}
      </dd>
    </div>
  );
}

/** Egy sor a belső ⇄ nyilvános szembeállításban. */
function CountRow({
  label,
  internal,
  unlocked,
}: {
  label: string;
  internal: number;
  unlocked: number;
}): JSX.Element {
  return (
    <tr>
      <th scope="row" className="py-2 text-left font-normal">
        {label}
      </th>
      <td className="py-2 font-medium tabular-nums">{numberFormat.format(internal)}</td>
      <td className="py-2 font-medium tabular-nums">
        {numberFormat.format(unlocked)}
        {internal > 0 && (
          <span className="text-subtle ml-1.5 font-normal">
            ({Math.round((unlocked / internal) * 100)}%)
          </span>
        )}
      </td>
    </tr>
  );
}

interface ContentSummary {
  spaces: number;
  categories: number;
  articles: number;
  publicSpaces: number;
  publicCategories: number;
  publicArticles: number;
  publicSpaceNames: string[];
}

/**
 * A fa összegzése darabszámokra. A NYILVÁNOS oldal a szerver `publiclyVisible` mezőjéből jön —
 * az a hierarchia mindhárom szintjét figyelembe vevő EFFEKTÍV érték, tehát egy belső szekcióban
 * álló „nyilvános" könyv itt helyesen NEM számít nyilvánosnak.
 *
 * A cikk-darabszám a csomópontok `articleCount`-jából adódik; nyilvános oldalon csak a NYILVÁNOS
 * könyvek cikkeit számoljuk. Ez FELSŐ becslés a súgón látható cikkekre: a cikk saját láthatósága
 * is `public` kell legyen, azt viszont a fa nem adja meg cikkenként. A `contentSubtitle` ezért
 * mondja ki a hármas feltételt — a szám tájékozódásra való, nem elszámolásra.
 */
function summarizeContent(spaces: SpaceNode[] | null | undefined): ContentSummary | null {
  if (!spaces) return null;
  const categories = spaces.flatMap((space) => space.categories);
  const publicSpaces = spaces.filter((space) => space.publiclyVisible);
  const publicCategories = categories.filter((category) => category.publiclyVisible);
  return {
    spaces: spaces.length,
    categories: categories.length,
    articles: categories.reduce((sum, category) => sum + category.articleCount, 0),
    publicSpaces: publicSpaces.length,
    publicCategories: publicCategories.length,
    publicArticles: publicCategories.reduce((sum, category) => sum + category.articleCount, 0),
    publicSpaceNames: publicSpaces.map((space) => space.name),
  };
}

/** Futásidő emberi alakban — a nyers másodperc-szám semmit nem mond. */
function formatUptime(seconds: number, t: TFunction): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return t('operations.uptimeDays', { days, hours });
  if (hours > 0) return t('operations.uptimeHours', { hours, minutes });
  return t('operations.uptimeMinutes', { minutes });
}
